import React, { useState, useEffect, useCallback } from "react";
import ListPagination, {
  LIST_DEFAULT_PAGE_SIZE,
  buildPageQuery,
  createPageChangeHandler,
  parseListFromPayload,
  parseTotalFromPayload,
} from "../../components/ListPagination";
import {
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  message,
  Tag,
  Spin,
  Space,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import API, {
  CONFIG,
  SYNC_MATCHES,
  updateMatchScoreUrl,
  approveMatchResultUrl,
} from "../../network/api";
import moment from "moment";
import dayjs from "dayjs";
import { TEXT_DEF } from "../../constant/text";
import {
  formatDateDashTimeDisplay,
  formatMatchDateAndKickoff,
  formatMomentDateDashTime,
} from "../../utils/formatDateDashTime";

const STATUS = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
};

const TEXT = {
  colIndex: "#",
  colMatch: "Trận đấu",
  colResult: "Kết quả thực tế",
  colDate: "Ngày",
  colStatus: "Trạng thái",
  colAction: "Hành động",
  pending: "Chờ xác nhận",
  confirmed: "Đã xác nhận",
  approve: "Duyệt",
  editTitle: "Cập nhật kết quả trận đấu",
  home: "Đội nhà",
  away: "Đội khách",
  save: "Lưu",
  cancel: "Hủy",
  noScore: "—",
  confirmApproveTitle: "Xác nhận duyệt kết quả?",
  confirmApproveDesc:
    "Sau khi duyệt, trạng thái sẽ là «Đã xác nhận» và không thể bấm Duyệt lại. Bạn chắc chắn chứ?",
  confirmOk: "Duyệt",
};

function formatScore(home, away) {
  if (home == null || away == null || Number.isNaN(home) || Number.isNaN(away)) {
    return TEXT.noScore;
  }
  return `${home} - ${away}`;
}

/** Ghép `matchDate` + `kickoff` (vd. "2026-05-21" + "20:00") hoặc dùng `matchAt`. */
function resolveKickoffMoment(row) {
  const matchDate = row?.matchDate;
  const kickoff = row?.kickoff != null ? String(row.kickoff).trim() : "";

  if (matchDate && kickoff) {
    const timePart = kickoff.match(/(\d{1,2}:\d{2})/);
    if (timePart) {
      const combined = moment(
        `${matchDate} ${timePart[1]}`,
        "YYYY-MM-DD HH:mm",
        true
      );
      if (combined.isValid()) return combined;
    }
    const dm = kickoff.match(/^(\d{1,2})\/(\d{1,2})\s+(\d{1,2}:\d{2})/);
    if (dm) {
      const y = moment(matchDate, "YYYY-MM-DD", true).isValid()
        ? moment(matchDate, "YYYY-MM-DD").year()
        : moment().year();
      const parsed = moment(
        `${dm[1]}/${dm[2]}/${y} ${dm[3]}`,
        "DD/MM/YYYY HH:mm",
        true
      );
      if (parsed.isValid()) return parsed;
    }
  }

  const matchAt = row?.matchAt;
  if (matchAt != null && matchAt !== "") {
    const m = moment(matchAt);
    if (m.isValid()) return m;
  }

  if (matchDate) {
    const d = moment(matchDate, "YYYY-MM-DD", true);
    if (d.isValid()) return d;
  }

  return null;
}

function formatKickoffFallback(val) {
  if (val == null || val === "") return "—";
  let m = moment(val);
  if (!m.isValid() && (typeof val === "number" || /^\d+$/.test(String(val)))) {
    const n = Number(val);
    m = moment(n < 1e12 ? n * 1000 : n);
  }
  if (m.isValid()) return formatMomentDateDashTime(m);
  const loose = moment(val);
  if (typeof val === "string") return formatDateDashTimeDisplay(val);
  return loose.isValid() ? formatMomentDateDashTime(loose) : "—";
}

/** Cột Ngày — `DD/MM - HH:mm` từ `matchDate` + `kickoff`. */
function formatMatchDateTimeDisplay(row) {
  const fromParts = formatMatchDateAndKickoff(row?.matchDate, row?.kickoff);
  if (fromParts !== "—") return fromParts;
  const m = resolveKickoffMoment(row);
  if (m && m.isValid()) return formatMomentDateDashTime(m);
  return formatKickoffFallback(row?.matchAt ?? row?.matchDate);
}

/** Chuẩn hoá điểm từ InputNumber / Form — luôn parse base-10 (tránh nhầm kiểu / hiển thị sai sau lưu) */
function parseScoreInput(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.trunc(raw);
  const n = parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) ? n : null;
}

/** matchId trong payload API — dạng số nếu parse được (vd. 292151) */
function toPayloadMatchId(id) {
  if (id == null || id === "") return id;
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
}

function mapApiItem(item, index) {
  const id = String(item?.matchId ?? item?.id ?? item?._id ?? index);
  const homeTeam = item?.homeTeam ?? item?.homeName ?? "";
  const awayTeam = item?.awayTeam ?? item?.awayName ?? "";
  const label =
    item?.matchLabel ||
    item?.label ||
    (homeTeam && awayTeam ? `${homeTeam} vs ${awayTeam}` : `Trận ${id}`);
  const rawStatus = item?.status ?? item?.confirmStatus;
  /** BE có thể trả `isResultApproved: true` thay cho status string */
  const isApprovedFlag =
    item?.isResultApproved === true ||
    item?.isResultApproved === "true" ||
    Number(item?.isResultApproved) === 1;
  const status =
    isApprovedFlag ||
    rawStatus === "confirmed" ||
    rawStatus === "CONFIRMED" ||
    rawStatus === true ||
    item?.confirmed === true
      ? STATUS.CONFIRMED
      : STATUS.PENDING;
  let matchDate = "";
  if (item?.matchDate != null && item.matchDate !== "") {
    matchDate =
      typeof item.matchDate === "string"
        ? item.matchDate
        : moment(item.matchDate).isValid()
          ? moment(item.matchDate).format("YYYY-MM-DD")
          : String(item.matchDate);
  }
  const matchAt =
    item?.matchAt ||
    item?.matchDate ||
    item?.scheduledAt ||
    item?.date ||
    null;
  if (!matchDate && matchAt != null && matchAt !== "") {
    const m = moment(matchAt);
    matchDate = m.isValid() ? m.format("YYYY-MM-DD") : "";
  }
  return {
    id,
    homeTeam,
    awayTeam,
    kickoff: item?.kickoff != null ? String(item.kickoff).trim() : "",
    group: item?.group != null ? String(item.group) : "",
    isAllowed: item?.isAllowed !== false,
    matchDate,
    matchLabel: label,
    homeScore:
      item?.homeScore != null ? parseScoreInput(item.homeScore) : item?.scoreHome != null ? parseScoreInput(item.scoreHome) : null,
    awayScore:
      item?.awayScore != null ? parseScoreInput(item.awayScore) : item?.scoreAway != null ? parseScoreInput(item.scoreAway) : null,
    matchAt,
    status,
  };
}

function getApiErrorMessage(err, fallback) {
  const d = err?.response?.data;
  if (d == null) return fallback;
  const m = d.message ?? d.error ?? d.msg;
  if (typeof m === "string" && m.trim()) return m;
  if (Array.isArray(m) && m.length) {
    const joined = m
      .map((x) => (typeof x === "string" ? x : ""))
      .filter(Boolean)
      .join(" ");
    if (joined.trim()) return joined;
  }
  return fallback;
}

const ConfirmResult = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_DEFAULT_PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveRecord, setApproveRecord] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [form] = Form.useForm();

  const onPageChange = createPageChangeHandler(page, pageSize, setPage, setPageSize);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get(`${CONFIG}`);
        const data = res.data?.data || res.data;
        const start = data?.schedule?.eventStart;
        const end = data?.schedule?.eventEnd;
        if (cancelled) return;
        setFromDate(
          start ? dayjs(start).startOf("day") : dayjs().startOf("day")
        );
        setToDate(end ? dayjs(end).endOf("day") : dayjs().endOf("day"));
      } catch {
        if (!cancelled) {
          setFromDate(dayjs().startOf("day"));
          setToDate(dayjs().endOf("day"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadList = useCallback(async () => {
    if (!fromDate?.isValid?.() || !toDate?.isValid?.()) return;
    setLoading(true);
    try {
      const { query } = buildPageQuery(page, pageSize);
      const url = `${SYNC_MATCHES}?${query}&fromDate=${fromDate.format(
        "DD-MM-YYYY"
      )}&toDate=${toDate.format("DD-MM-YYYY")}`;
      const res = await API.get(url);
      const payload = res.data?.data ?? res.data;
      const list = parseListFromPayload(payload);
      setRows(list.map((item, i) => mapApiItem(item, i)));
      setTotalRecords(parseTotalFromPayload(payload, list.length));
    } catch (err) {
      console.warn(err);
      setRows([]);
      setTotalRecords(0);
      messageApi.error(
        getApiErrorMessage(err, "Không tải được danh sách trận đấu.")
      );
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, fromDate, toDate, messageApi]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      homeScore: record.homeScore ?? undefined,
      awayScore: record.awayScore ?? undefined,
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const submitEdit = async () => {
    try {
      const v = await form.validateFields();
      const home = parseScoreInput(v.homeScore);
      const away = parseScoreInput(v.awayScore);
      if (home == null || away == null) {
        messageApi.error("Tỉ số không hợp lệ.");
        return;
      }
      if (!editing) {
        messageApi.error("Không có trận đang chỉnh sửa.");
        return;
      }
      const matchIdForUrl = toPayloadMatchId(editing.id);
      setSaving(true);
      await API.post(updateMatchScoreUrl(matchIdForUrl), {
        homeScore: home,
        awayScore: away,
      });
      messageApi.success("Đã cập nhật kết quả. Thay đổi sẽ đồng bộ lên Client.");
      closeEdit();
      await loadList();
    } catch (e) {
      if (e?.errorFields) return;
      if (e?.response) {
        messageApi.error(
          getApiErrorMessage(e, "Không thể lưu tỉ số. Vui lòng thử lại.")
        );
        return;
      }
      messageApi.error("Không thể lưu. Vui lòng thử lại.");
    } finally {
      setSaving(false);
    }
  };

  const openApproveModal = (record) => {
    setApproveRecord(record);
    setApproveOpen(true);
  };

  const closeApproveModal = () => {
    setApproveOpen(false);
    setApproveRecord(null);
  };

  const submitApprove = async () => {
    const record = approveRecord;
    if (!record || record.status === STATUS.CONFIRMED) return;
    setApproveLoading(true);
    try {
      await API.post(approveMatchResultUrl(toPayloadMatchId(record.id)));
      messageApi.success("Đã xác nhận kết quả trận đấu.");
      closeApproveModal();
      await loadList();
    } catch (apiErr) {
      console.warn(apiErr);
      messageApi.error(
        getApiErrorMessage(apiErr, "Không thể duyệt kết quả. Vui lòng thử lại.")
      );
    } finally {
      setApproveLoading(false);
    }
  };

  const columns = [
    {
      title: TEXT.colIndex,
      key: "index",
      width: 56,
      render: (_, __, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: TEXT.colMatch,
      dataIndex: "matchLabel",
      key: "matchLabel",
      ellipsis: true,
    },
    {
      title: TEXT.colResult,
      key: "result",
      width: 200,
      render: (_, record) => (
        <Space size="middle" wrap>
          <span>{formatScore(record.homeScore, record.awayScore)}</span>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
            aria-label="Chỉnh sửa kết quả"
          />
        </Space>
      ),
    },
    {
      title: TEXT.colDate,
      key: "matchDateTime",
      width: 160,
      align: "center",
      render: (_, record) => formatMatchDateTimeDisplay(record),
    },
    {
      title: TEXT.colStatus,
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (s) =>
        s === STATUS.CONFIRMED ? (
          <Tag color="success">{TEXT.confirmed}</Tag>
        ) : (
          <Tag color="warning">{TEXT.pending}</Tag>
        ),
    },
    {
      title: TEXT.colAction,
      key: "action",
      width: 120,
      render: (_, record) =>
        record.status === STATUS.CONFIRMED ? (
          <Button type="primary" size="small" disabled>
            {TEXT.approve}
          </Button>
        ) : (
          <Button type="primary" size="small" onClick={() => openApproveModal(record)}>
            {TEXT.approve}
          </Button>
        ),
    },
  ];

  return (
    <div className="col-12" style={{ padding: "8px 0" }}>
      {contextHolder}
      <Spin spinning={loading}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          pagination={false}
          bordered
          locale={{ emptyText: TEXT_DEF.text0035 }}
          scroll={{ x: "max-content" }}
        />
        <ListPagination
          current={page}
          total={totalRecords}
          pageSize={pageSize}
          onChange={onPageChange}
          unit="trận đấu"
        />
      </Spin>

      {/* Cùng kiểu Modal như trang Quản lý popup fake: căn giữa viewport, footer Hủy + nút primary */}
      <Modal
        title={TEXT.confirmApproveTitle}
        open={approveOpen}
        onCancel={closeApproveModal}
        onOk={submitApprove}
        okText={TEXT.confirmOk}
        cancelText={TEXT.cancel}
        confirmLoading={approveLoading}
        centered
        destroyOnClose
        maskClosable={false}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 12, color: "rgba(0,0,0,0.65)" }}>{TEXT.confirmApproveDesc}</p>
          <div style={{ marginBottom: 0 }}>
            <span style={{ display: "block", marginBottom: 6 }}>Trận đấu:</span>
            <strong style={{ fontSize: 15 }}>{approveRecord?.matchLabel ?? "—"}</strong>
          </div>
        </div>
      </Modal>

      <Modal
        title={TEXT.editTitle}
        open={editOpen}
        onCancel={closeEdit}
        onOk={submitEdit}
        confirmLoading={saving}
        okText={TEXT.save}
        cancelText={TEXT.cancel}
        centered
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="homeScore"
            label={TEXT.home}
            rules={[{ required: true, message: "Nhập tỉ số đội nhà" }]}
          >
            <InputNumber min={0} max={99} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="awayScore"
            label={TEXT.away}
            rules={[{ required: true, message: "Nhập tỉ số đội khách" }]}
          >
            <InputNumber min={0} max={99} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ConfirmResult;
